import { useContext, useEffect, useState } from "react";
import { DataContext } from "../../contexts/DataProvider";
import { useAlert } from "../../functions/useAlert";
import {
  fetchConnectorByOrg,
  toggleConnectorStatus,
  toggleEmailStatus,
  toggleAutoRecording,
  toggleOutlookContactsStatus,
  getMicrosoft365OAuthUrl,
  disconnectMicrosoft365Org,
  saveIntellifloCredentials,
  createConnector,
  updateConnector,
} from "../../services/connectors.service";
import { fetchOrganisations as fetchOrgList } from "../../services/organisations.service";

interface UseConnectorsOptions {
  selectedOrganization: string;
  onOrganizationChange: (orgId: string) => void;
  onStorageProviderChange: (provider: string) => void;
}

export function useConnectors({
  selectedOrganization,
  onOrganizationChange,
  onStorageProviderChange,
}: UseConnectorsOptions) {
  const { login }: any = useContext(DataContext);
  const [setAlert] = useAlert();

  const [connectorTabValue, setConnectorTabValue] = useState(0);
  const [isConnectorModalOpen, setIsConnectorModalOpen] =
    useState<boolean>(false);
  const [currentConnector, setCurrentConnector] = useState<any>(null);
  const [connectorInputs, setConnectorInputs] = useState<Record<string, any>>(
    {},
  );
  const [originalConnectorInputs, setOriginalConnectorInputs] = useState<
    Record<string, any>
  >({});
  const [connectorAuthorization, setConnectorAuthorization] = useState<
    Record<string, any>
  >({});
  const [isConnectorLoading, setIsConnectorLoading] = useState<boolean>(false);
  const [confirmationAction, setConfirmationAction] = useState<{
    open: boolean;
    type: string;
    connector: any;
  }>({
    open: false,
    type: "",
    connector: null,
  });
  const [isEmailEnabled, setIsEmailEnabled] = useState(false);
  const [isOutlookContactsEnabled, setIsOutlookContactsEnabled] =
    useState(true);
  const [isAutoRecordingEnabled, setIsAutoRecordingEnabled] = useState(false);
  const [autoRecordingSetupOpen, setAutoRecordingSetupOpen] = useState(false);

  const handleChangeForAzure = async (event: any) => {
    const orgId = event.target.value;
    onOrganizationChange(orgId);
    setConnectorAuthorization({});

    if (orgId === "All Organisation") {
      return;
    }

    try {
      const response: any = await fetchConnectorByOrg(orgId);
      const { message, ...connectorData } = response;

      const newAuthState: Record<string, any> = {};
      for (const key in connectorData) {
        if (Object.prototype.hasOwnProperty.call(connectorData, key)) {
          newAuthState[key] = { ...connectorData[key], isAuthorized: true };
        }
      }
      setConnectorAuthorization(newAuthState);

      const orgList: any = await fetchOrgList();
      const selectedOrg = orgList.organisations.find(
        (o: any) => o._id === orgId,
      );
      if (selectedOrg) {
        const provider = selectedOrg.storageSettings?.provider || "AZURE";
        onStorageProviderChange(provider);
      }
    } catch (error) {
      console.error("Error fetching connector details:", error);
      setConnectorAuthorization({});
    }
  };

  // Refresh connector auth state when org or login role changes
  useEffect(() => {
    if (login?.data?.role === "orgadmin" && login?.data?.orgId) {
      handleChangeForAzure({ target: { value: login.data.orgId } });
    } else if (
      login?.data?.role === "global admin" &&
      selectedOrganization !== "All Organisation"
    ) {
      handleChangeForAzure({ target: { value: selectedOrganization } });
    }
  }, [login?.data?.orgId, selectedOrganization, login?.data?.role]);

  // Sync M365 toggle states when connector auth data loads
  useEffect(() => {
    if (connectorAuthorization?.microsoft_365) {
      const m365 = connectorAuthorization.microsoft_365;
      setIsEmailEnabled(Boolean(m365.emailStatus));
      setIsAutoRecordingEnabled(Boolean(m365.autoRecordingEnabled));
      setIsOutlookContactsEnabled(
        m365.outlookContactsEnabled === undefined ||
          m365.outlookContactsEnabled === null
          ? true
          : Boolean(m365.outlookContactsEnabled),
      );
    } else {
      setIsEmailEnabled(true);
      setIsAutoRecordingEnabled(false);
      setIsOutlookContactsEnabled(true);
    }
  }, [connectorAuthorization]);

  const handleCloseConnectorModal = () => {
    setIsConnectorModalOpen(false);
    setCurrentConnector(null);
    setConnectorInputs({});
    setOriginalConnectorInputs({});
  };

  const handleCardClick = (connector: any) => {
    if (connector.authType === "oauth") {
      return;
    }

    const connectorKey = connector.name
      .replace(/ /g, "_")
      .replace(/\./g, "")
      .toLowerCase();

    setCurrentConnector(connector);

    const existingConfig = connectorAuthorization[connectorKey] || {};
    const cleanedInputs: Record<string, any> = {};

    connector.fields.forEach((field: any) => {
      const value = existingConfig[field.name];
      const upperFieldName = field.name.toUpperCase();
      const upperValue = existingConfig[upperFieldName];

      if (
        field.type === "password" &&
        (value === "***CONFIGURED***" || upperValue === "***CONFIGURED***")
      ) {
        cleanedInputs[field.name] = "••••••••••••";
      } else if (field.type === "password" && upperValue) {
        cleanedInputs[field.name] = "••••••••••••";
      } else {
        cleanedInputs[field.name] = value || "";
      }
    });

    setConnectorInputs(cleanedInputs);
    setOriginalConnectorInputs(cleanedInputs);
    setIsConnectorModalOpen(true);

    if (connector.id === "microsoft_365") {
      const emailStatus: boolean = Boolean(
        connectorAuthorization[connectorKey]?.emailStatus,
      );
      setIsEmailEnabled(emailStatus);

      const autoRecordingStatus: boolean = Boolean(
        connectorAuthorization[connectorKey]?.autoRecordingEnabled,
      );
      setIsAutoRecordingEnabled(autoRecordingStatus);
    }
  };

  const handleAdviserLogicToggle = (connector: any) => {
    setCurrentConnector(connector);
    setConnectorInputs({});
    setOriginalConnectorInputs({});
    setIsConnectorModalOpen(true);
  };

  const handleToggle = (e: React.MouseEvent, connector: any) => {
    e.stopPropagation();

    const connectorKey = connector.name
      .replace(/ /g, "_")
      .replace(/\./g, "")
      .toLowerCase();
    const isConfigured = !!connectorAuthorization[connectorKey];
    const currentStatus = connectorAuthorization[connectorKey]?.status;

    if (isConfigured) {
      setConfirmationAction({
        open: true,
        type: currentStatus ? "disable" : "enable",
        connector,
      });
    } else {
      if (connector.authType === "oauth") {
        const token = localStorage.getItem("token");
        if (token) {
          const frontendRedirectUrl = encodeURIComponent(window.location.href);
          switch (connector.id) {
            case "worksorted":
              // OAuth initiation — must be a full-page redirect (external backend endpoint)
              window.location.href = `/api/worksorted-auth/connect?token=${token}&frontendRedirectUrl=${frontendRedirectUrl}`;
              return;
            default:
              setAlert("Unknown OAuth connector", "error");
              return;
          }
        } else {
          setAlert(
            "Authentication token not found. Please sign in again.",
            "error",
          );
        }
      } else {
        setCurrentConnector(connector);
        const existingConfig = connectorAuthorization[connector.id] || {};
        const cleanedInputs: Record<string, any> = {};

        connector.fields.forEach((field: any) => {
          const value = existingConfig[field.name];
          if (field.type === "password" && value === "***CONFIGURED***") {
            cleanedInputs[field.name] = "";
          } else {
            cleanedInputs[field.name] = value || "";
          }
        });

        setConnectorInputs(cleanedInputs);
        setOriginalConnectorInputs(cleanedInputs);
        setIsConnectorModalOpen(true);
      }
    }
  };

  const handleConfirmToggle = async () => {
    const { type, connector } = confirmationAction;
    if (!type || !connector) return;

    try {
      await toggleConnectorStatus({
        connectorName: connector.name,
        orgId: selectedOrganization,
      });
      setAlert(`Connector ${type}d successfully!`, "success");
      handleChangeForAzure({ target: { value: selectedOrganization } });
    } catch (error: any) {
      setAlert(error.message || `Failed to ${type} connector.`, "error");
    } finally {
      setConfirmationAction({ open: false, type: "", connector: null });
    }
  };

  const toggleEmailStatusHandler = async () => {
    if (!selectedOrganization) return;
    try {
      await toggleEmailStatus(selectedOrganization);
      setIsEmailEnabled((prev) => !prev);
      setConnectorAuthorization((prev: any) => ({
        ...prev,
        microsoft_365: {
          ...prev.microsoft_365,
          emailStatus: !isEmailEnabled,
        },
      }));
      setAlert("Email status updated successfully!", "success");
    } catch (error: any) {
      setAlert(error.message || "Failed to update email status.", "error");
    }
  };

  const toggleAutoRecordingStatusHandler = async () => {
    try {
      const newStatus = !isAutoRecordingEnabled;
      await toggleAutoRecording({ orgId: selectedOrganization });
      setIsAutoRecordingEnabled(newStatus);
      setConnectorAuthorization((prev: any) => ({
        ...prev,
        microsoft_365: {
          ...prev.microsoft_365,
          autoRecordingEnabled: newStatus,
        },
      }));
      setAlert("Auto-recording status updated successfully!", "success");
    } catch (error: any) {
      setAlert(
        error.message || "Failed to update auto-recording status.",
        "error",
      );
    }
  };

  const toggleOutlookContactsStatusHandler = async () => {
    try {
      const newStatus = !isOutlookContactsEnabled;
      await toggleOutlookContactsStatus(selectedOrganization);
      setIsOutlookContactsEnabled(newStatus);
      setConnectorAuthorization((prev: any) => ({
        ...prev,
        microsoft_365: {
          ...prev.microsoft_365,
          outlookContactsEnabled: newStatus,
        },
      }));
      setAlert(
        newStatus
          ? "Outlook contact import enabled."
          : "Outlook contact import disabled.",
        "success",
      );
    } catch (error: any) {
      setAlert(
        error.message || "Failed to update Outlook contact import status.",
        "error",
      );
    }
  };

  const handleConnectMicrosoft365 = async () => {
    if (!selectedOrganization) return;
    setIsConnectorLoading(true);
    try {
      const redirectAfterOAuth = `${window.location.origin}/admin?ms365_org=${selectedOrganization}&tab=connectors`;
      const response: any = await getMicrosoft365OAuthUrl(redirectAfterOAuth);
      if (response?.url) {
        // OAuth flow — must be a full-page redirect to Microsoft's login page
        window.location.href = response.url;
      } else {
        setAlert(
          "Could not start Microsoft 365 connection. Please try again.",
          "error",
        );
        setIsConnectorLoading(false);
      }
    } catch (error: any) {
      setAlert(
        error.message || "Failed to start Microsoft 365 connection.",
        "error",
      );
      setIsConnectorLoading(false);
    }
  };

  const handleDisconnectMicrosoft365 = async () => {
    if (!selectedOrganization) return;
    setIsConnectorLoading(true);
    try {
      await disconnectMicrosoft365Org();
      setAlert("Microsoft 365 disconnected.", "success");
      handleChangeForAzure({ target: { value: selectedOrganization } });
    } catch (error: any) {
      setAlert(error.message || "Failed to disconnect Microsoft 365.", "error");
    } finally {
      setIsConnectorLoading(false);
    }
  };

  const handleAuthorizeConnector = async () => {
    if (!currentConnector) return;

    if (currentConnector.id === "intelliflo") {
      const { clientId, clientSecret, apiKey } = connectorInputs;
      if (!clientId || !clientSecret || !apiKey) {
        setAlert(
          "All fields (Client ID, Secret, API Key) are required",
          "error",
        );
        return;
      }
      setIsConnectorLoading(true);
      try {
        await saveIntellifloCredentials({
          clientId: clientId.trim(),
          clientSecret: clientSecret.trim(),
          apiKey: apiKey.trim(),
        });
        setAlert("Intelliflo connected successfully!", "success");
        if (selectedOrganization) {
          handleChangeForAzure({ target: { value: selectedOrganization } });
        }
        handleCloseConnectorModal();
      } catch (error: any) {
        setAlert(
          error.message ||
            "Failed to connect to Intelliflo. Please check your keys.",
          "error",
        );
      } finally {
        setIsConnectorLoading(false);
      }
      return;
    }

    if (currentConnector.id === "microsoft_365" && currentConnector.useOAuth) {
      return;
    }

    const cleanConfig: Record<string, any> = {};
    currentConnector.fields.forEach((field: any) => {
      const value = connectorInputs[field.name];
      if (value !== undefined && value !== "" && value !== "••••••••••••") {
        cleanConfig[field.name] = value;
      }
    });

    const connectorId = connectorAuthorization[currentConnector.id]?._id;
    if (connectorId && Object.keys(cleanConfig).length === 0) {
      setAlert("No changes to save.", "info");
      return;
    }

    setIsConnectorLoading(true);
    try {
      const formData: Record<string, any> = {
        connectorName: currentConnector.name,
        orgId: selectedOrganization,
        userId: login?.data?.id,
        config: cleanConfig,
        ...(currentConnector.id === "zoho" && {
          capabilities: ["CRM"],
          displayCategory: "CRM",
          scope: "ORGANISATION",
        }),
      };

      const response: any = connectorId
        ? await updateConnector(connectorId, formData)
        : await createConnector(formData);

      // Never call an unverified save "connected" (CONN-02). For Worksorted and
      // Zoho these org-level values are OAuth app-registration details, and the
      // connection itself is a separate per-user authorisation step, so the
      // toast has to stop short of claiming a working integration.
      const unverified = response?.credentialsVerified === false;
      setAlert(
        unverified
          ? `${currentConnector.name} settings saved, but the connection is not verified yet — authorise ${currentConnector.name} to finish connecting.`
          : `Connector ${connectorId ? "updated" : "created"} successfully!`,
        unverified ? "info" : "success",
      );
      handleCloseConnectorModal();
      handleChangeForAzure({ target: { value: selectedOrganization } });
    } catch (error: any) {
      // The backend puts the provider's own reason in `description`; without it
      // the user only sees "Could not connect to X." with no way to act on it.
      const detail = error?.data?.description;
      setAlert(
        detail
          ? `${error.message} ${detail}`
          : error.message || "Failed to save connector.",
        "error",
      );
    } finally {
      setIsConnectorLoading(false);
    }
  };

  return {
    connectorTabValue,
    setConnectorTabValue,
    isConnectorModalOpen,
    setIsConnectorModalOpen,
    currentConnector,
    setCurrentConnector,
    connectorInputs,
    setConnectorInputs,
    originalConnectorInputs,
    setOriginalConnectorInputs,
    connectorAuthorization,
    setConnectorAuthorization,
    isConnectorLoading,
    confirmationAction,
    setConfirmationAction,
    isEmailEnabled,
    isOutlookContactsEnabled,
    isAutoRecordingEnabled,
    autoRecordingSetupOpen,
    setAutoRecordingSetupOpen,
    handleChangeForAzure,
    handleCloseConnectorModal,
    handleCardClick,
    handleAdviserLogicToggle,
    handleToggle,
    handleConfirmToggle,
    toggleEmailStatus: toggleEmailStatusHandler,
    toggleAutoRecordingStatus: toggleAutoRecordingStatusHandler,
    toggleOutlookContactsStatus: toggleOutlookContactsStatusHandler,
    handleConnectMicrosoft365,
    handleDisconnectMicrosoft365,
    handleAuthorizeConnector,
  };
}
